import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    Avatar,
    Tabs,
    Tab,
    CircularProgress,
    Alert,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    LinearProgress,
    Divider,
} from '@mui/material';
import {
    Search as SearchIcon,
    Person as PersonIcon,
    Assessment as AssessmentIcon,
    Login as LoginIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    FilterList as FilterListIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    AccessTime as AccessTimeIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    LocationOn as LocationOnIcon,
    Devices as DevicesIcon,
    DateRange as DateRangeIcon,
    Clear as ClearIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    Sort as SortIcon,
} from '@mui/icons-material';
// Removed date picker imports due to date-fns version conflict
import axios from 'axios';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

const UserHistory = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [tabValue, setTabValue] = useState(0);
    const [dateFilter, setDateFilter] = useState({
        startDate: null,
        endDate: null,
    });
    const [statusFilter, setStatusFilter] = useState('all');
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });

    useEffect(() => {
        fetchUsersOverview();
    }, []);

    const fetchUsersOverview = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/user-history/users-overview`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );
            setUsers(response.data.data);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to fetch users data');
        } finally {
            setLoading(false);
        }
    };

    const fetchUserDetails = async (userId) => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/user-history/user/${userId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );
            setUserDetails(response.data.data);
            setSelectedUser(response.data.data.user);
            setDetailsDialogOpen(true);
        } catch (err) {
            console.error('Error fetching user details:', err);
            setError('Failed to fetch user details');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchUsersOverview();
        setRefreshing(false);
    };

    const handleExportData = () => {
        const csvContent = [
            ['Name', 'Email', 'Phone', 'Location', 'Last Login', 'Total Tests', 'Last Test Score', 'Account Created'],
            ...sortedAndFilteredUsers.map(user => [
                `${user.firstName} ${user.lastName}`,
                user.email,
                user.phone || 'N/A',
                `${user.city || ''} ${user.state || ''}`.trim() || 'N/A',
                user.lastLogin ? format(parseISO(user.lastLogin), 'MMM dd, yyyy HH:mm') : 'Never',
                user.totalTests,
                user.lastTest ? `${user.lastTest.score}%` : 'N/A',
                format(parseISO(user.createdAt), 'MMM dd, yyyy'),
            ]),
        ];

        const csv = csvContent.map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `user-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const getStatusChip = (status, type = 'login') => {
        if (!status) return null;

        if (type === 'login') {
            const config = {
                success: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
                failed: { color: 'error', icon: <CancelIcon fontSize="small" /> },
                suspicious: { color: 'warning', icon: <FilterListIcon fontSize="small" /> },
            };
            const { color, icon } = config[status] || config.success;
            return (
                <Chip
                    label={status}
                    color={color}
                    size="small"
                    icon={icon}
                    sx={{ textTransform: 'capitalize' }}
                />
            );
        }

        if (type === 'test') {
            return (
                <Chip
                    label={status ? 'Passed' : 'Failed'}
                    color={status ? 'success' : 'error'}
                    size="small"
                    icon={status ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                />
            );
        }
    };

    const getScoreBadge = (score) => {
        if (score >= 90) {
            return { color: '#4caf50', label: 'Excellent' };
        } else if (score >= 70) {
            return { color: '#2196f3', label: 'Good' };
        } else if (score >= 50) {
            return { color: '#ff9800', label: 'Fair' };
        } else {
            return { color: '#f44336', label: 'Poor' };
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
        }
        return <SortIcon fontSize="small" sx={{ opacity: 0.5 }} />;
    };

    const sortedAndFilteredUsers = useMemo(() => {
        let filtered = users.filter(user => {
            const matchesSearch =
                user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.phone?.includes(searchTerm);

            const matchesStatus = statusFilter === 'all' ||
                (statusFilter === 'active' && user.lastLogin) ||
                (statusFilter === 'inactive' && !user.lastLogin);

            return matchesSearch && matchesStatus;
        });

        if (sortConfig.key) {
            filtered = filtered.sort((a, b) => {
                let aValue, bValue;

                switch (sortConfig.key) {
                    case 'name':
                        aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
                        bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
                        break;
                    case 'email':
                        aValue = a.email?.toLowerCase() || '';
                        bValue = b.email?.toLowerCase() || '';
                        break;
                    case 'lastLogin':
                        aValue = a.lastLogin ? new Date(a.lastLogin) : new Date(0);
                        bValue = b.lastLogin ? new Date(b.lastLogin) : new Date(0);
                        break;
                    case 'totalLogins':
                        aValue = a.totalLogins || 0;
                        bValue = b.totalLogins || 0;
                        break;
                    case 'totalTests':
                        aValue = a.totalTests || 0;
                        bValue = b.totalTests || 0;
                        break;
                    case 'lastTestScore':
                        aValue = a.lastTest?.score || 0;
                        bValue = b.lastTest?.score || 0;
                        break;
                    case 'createdAt':
                        aValue = new Date(a.createdAt);
                        bValue = new Date(b.createdAt);
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    }, [users, searchTerm, statusFilter, sortConfig]);

    const UserDetailsDialog = () => {
        if (!userDetails) return null;

        const { user, stats, loginHistory, testScores } = userDetails;

        return (
            <Dialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: '#ff5e1c', width: 56, height: 56 }}>
                                {user.firstName[0]}{user.lastName[0]}
                            </Avatar>
                            <Box>
                                <Typography variant="h6">
                                    {user.firstName} {user.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {user.email}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => setDetailsDialogOpen(false)}>
                            <ClearIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} sx={{ mb: 3 }}>
                        <Tab label="Overview" />
                        <Tab label="Login History" />
                        <Tab label="Test Scores" />
                    </Tabs>

                    {tabValue === 0 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <Card>
                                            <CardContent>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <LoginIcon color="primary" />
                                                    <Typography variant="subtitle2">Total Logins</Typography>
                                                </Box>
                                                <Typography variant="h4" sx={{ mt: 1 }}>
                                                    {stats.totalLogins}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Card>
                                            <CardContent>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <AssessmentIcon color="primary" />
                                                    <Typography variant="subtitle2">Total Tests</Typography>
                                                </Box>
                                                <Typography variant="h4" sx={{ mt: 1 }}>
                                                    {stats.totalTests}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Card>
                                            <CardContent>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <TrendingUpIcon color="success" />
                                                    <Typography variant="subtitle2">Avg Score</Typography>
                                                </Box>
                                                <Typography variant="h4" sx={{ mt: 1 }}>
                                                    {stats.averageScore}%
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Card>
                                            <CardContent>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <CheckCircleIcon color="success" />
                                                    <Typography variant="subtitle2">Pass Rate</Typography>
                                                </Box>
                                                <Typography variant="h4" sx={{ mt: 1 }}>
                                                    {stats.passRate}%
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Contact Information
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Phone</Typography>
                                            <Typography>{user.phone || 'Not provided'}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Email</Typography>
                                            <Typography>{user.email}</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">Address</Typography>
                                            <Typography>
                                                {user.address || 'Not provided'}
                                                {user.city && user.state && <br />}
                                                {user.city && `${user.city}, `}
                                                {user.state} {user.zip}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Account Details
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Created</Typography>
                                            <Typography>
                                                {format(parseISO(user.createdAt), 'MMM dd, yyyy')}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Last Active</Typography>
                                            <Typography>
                                                {stats.lastActive
                                                    ? formatDistanceToNow(parseISO(stats.lastActive), { addSuffix: true })
                                                    : 'Never'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}

                    {tabValue === 1 && (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date & Time</TableCell>
                                        <TableCell>IP Address</TableCell>
                                        <TableCell>Device</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell>Duration</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loginHistory?.map((login, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                {format(parseISO(login.loginTime), 'MMM dd, yyyy HH:mm')}
                                            </TableCell>
                                            <TableCell>{login.ipAddress || 'N/A'}</TableCell>
                                            <TableCell>
                                                {login.deviceInfo ?
                                                    `${login.deviceInfo.browser} on ${login.deviceInfo.os}` :
                                                    'Unknown'}
                                            </TableCell>
                                            <TableCell>
                                                {login.location?.city ?
                                                    `${login.location.city}, ${login.location.country}` :
                                                    'Unknown'}
                                            </TableCell>
                                            <TableCell>{login.formattedDuration || 'Active'}</TableCell>
                                            <TableCell>{getStatusChip(login.status, 'login')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {tabValue === 2 && (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Test Name</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Score</TableCell>
                                        <TableCell>Questions</TableCell>
                                        <TableCell>Time Taken</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {testScores?.map((test, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{test.testName}</TableCell>
                                            <TableCell>
                                                {format(parseISO(test.completedAt), 'MMM dd, yyyy')}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={test.percentage}
                                                        sx={{
                                                            width: 60,
                                                            height: 8,
                                                            borderRadius: 4,
                                                            backgroundColor: '#e0e0e0',
                                                            '& .MuiLinearProgress-bar': {
                                                                backgroundColor: getScoreBadge(test.percentage).color,
                                                            },
                                                        }}
                                                    />
                                                    <Typography variant="body2">
                                                        {test.percentage}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {test.correctAnswers}/{test.totalQuestions}
                                            </TableCell>
                                            <TableCell>{test.formattedTimeTaken || 'N/A'}</TableCell>
                                            <TableCell>{getStatusChip(test.passed, 'test')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    User Activity History
                </Typography>
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={refreshing}
                    >
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportData}
                        sx={{ backgroundColor: '#ff5e1c' }}
                    >
                        Export CSV
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by name, email, or phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Status Filter</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                label="Status Filter"
                            >
                                <MenuItem value="all">All Users</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Box display="flex" justifyContent="center" py={5}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell
                                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('name')}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            User
                                            {getSortIcon('name')}
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('email')}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            Contact
                                            {getSortIcon('email')}
                                        </Box>
                                    </TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell
                                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('lastLogin')}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            Last Login
                                            {getSortIcon('lastLogin')}
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('totalLogins')}
                                    >
                                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                            Total Logins
                                            {getSortIcon('totalLogins')}
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('totalTests')}
                                    >
                                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                            Total Tests
                                            {getSortIcon('totalTests')}
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('lastTestScore')}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            Last Test
                                            {getSortIcon('lastTestScore')}
                                        </Box>
                                    </TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedAndFilteredUsers
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((user) => (
                                        <TableRow key={user._id} hover>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar sx={{ bgcolor: '#ff5e1c', width: 32, height: 32 }}>
                                                        {user.firstName?.[0]}{user.lastName?.[0]}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {user.firstName} {user.lastName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {user.isAdmin && (
                                                                <Chip label="Admin" size="small" color="primary" sx={{ mr: 1 }} />
                                                            )}
                                                            Member since {format(parseISO(user.createdAt), 'MMM yyyy')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{user.email}</Typography>
                                                <Typography variant="caption">{user.phone || 'No phone'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                {user.city || user.state ? (
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <LocationOnIcon fontSize="small" color="action" />
                                                        <Typography variant="body2">
                                                            {user.city}{user.city && user.state && ', '}{user.state}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Not provided
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {user.lastLogin ? (
                                                    <Tooltip title={format(parseISO(user.lastLogin), 'PPpp')}>
                                                        <Typography variant="body2">
                                                            {formatDistanceToNow(parseISO(user.lastLogin), { addSuffix: true })}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Never logged in
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={user.totalLogins} variant="outlined" size="small" />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={user.totalTests} variant="outlined" size="small" />
                                            </TableCell>
                                            <TableCell>
                                                {user.lastTest ? (
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {user.lastTest.name}
                                                        </Typography>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Chip
                                                                label={`${user.lastTest.score}%`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: getScoreBadge(user.lastTest.score).color,
                                                                    color: 'white',
                                                                }}
                                                            />
                                                            {getStatusChip(user.lastTest.passed, 'test')}
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No tests taken
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => fetchUserDetails(user._id)}
                                                    sx={{
                                                        borderColor: '#ff5e1c',
                                                        color: '#ff5e1c',
                                                        '&:hover': {
                                                            borderColor: '#ff5e1c',
                                                            backgroundColor: 'rgba(255, 94, 28, 0.04)',
                                                        },
                                                    }}
                                                >
                                                    View Details
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={sortedAndFilteredUsers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </Paper>
            )}

            <UserDetailsDialog />
        </Container>
    );
};

export default UserHistory;